import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";

import SideBar from "../../../components/SideBar";
import BackButton from "../../../components/Base/BackButton";
import PageHeader from "../../../components/AdminCoachPanel/coaches/PageHeader/PageHeader";
import Pagination from "../../../components/AdminCoachPanel/coaches/Pagination/Pagination";
import DateRangePicker from "../../../components/AdminCoachPanel/coaches/DateRangePicker/DateRangePicker";
import {
  GetAllCoachMeetings,
  GetAllCoaches,
  GetCoachStudents,
} from "../../../api/AdminCoachPanel/coachApis";
import { Components } from "../../../assets/Dimensions";
import ExamCategoryType from "../../../enums/ExamCategoryType";

// Reuse existing styling: page wrapper, profile card/breadcrumb, and the exact
// same meetings table/modal styling as the coach profile "Toplantılar" tab.
import "../Coaches.css";
import "../../Coaches/CoachProfilePage.css";
import "../../Coaches/Components/meetingTabView/MeetingsTabView.css";
import "./AllCoachMeetings.css";

const PAGE_SIZE = 15;
const TEXT_FILTER_DEBOUNCE_MS = 400;

/* ---------------------------------------------------------------------------
 * Dynamic table config
 *
 * The table is built from whatever keys the backend returns. A small set of
 * "core" keys get rich, purpose-built rendering + filters (date split, coach /
 * student dropdowns, recording modal). Every OTHER key in the response becomes
 * a plain text column with a text filter automatically — so when the backend
 * adds a field like PromotionName, it shows up and becomes filterable with no
 * frontend change. The filter value is sent to the server under a camelCase
 * param derived from the key (PromotionName -> promotionName); the server only
 * needs to accept that param.
 * ------------------------------------------------------------------------- */

// Raw keys already consumed by the core (hand-built) columns.
const CORE_CONSUMED_KEYS = new Set([
  "Date",
  "CoachName",
  "CoachSurname",
  "CoachUserId",
  "StudentName",
  "StudentSurname",
  "StudentUserId",
  "Title",
  "Recordings",
  "Ratings",
]);

// Rating answer types: type 1 is a numeric scale rendered as "N/5"; everything
// else is free-text and rendered as-is.
const RATING_SCALE_ANSWER_TYPE = 1;
const RATING_SCALE_MAX = 5;

// Technical keys we never want to show as a column.
const HIDDEN_KEYS = new Set(["MeetingId"]);

// Optional nicer (Turkish) headers for auto-detected columns; anything not here
// falls back to a humanized version of the key.
const AUTO_COLUMN_LABELS = {
  PromotionName: "Promosyon",
};

// ExamCategoryType ({ YKS: 1, LGS: 2, ... }) -> id -> name for coach labels.
const EXAM_CATEGORY_LABEL = Object.keys(ExamCategoryType).reduce((acc, key) => {
  acc[ExamCategoryType[key]] = key;
  return acc;
}, {});

function examCategoryLabel(id) {
  const label = EXAM_CATEGORY_LABEL[id];
  return label && label !== "None" ? label : null;
}

// Local Date -> "YYYY-MM-DD" (query param the server filters on).
function toDateParam(d) {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Join a first/last name pair, treating the backend's "-" placeholder and
// blanks as empty; returns "-" when nothing meaningful remains.
function joinName(first, last) {
  const parts = [first, last].filter((x) => x && x !== "-");
  const joined = parts.join(" ").trim();
  return joined || "-";
}

// "2026-09-10T18:00:00.46" -> { day: "2026-09-10", time: "18:00" }. Split
// directly (no Date parsing) to show the backend's wall-clock without UTC drift.
function splitDateTime(value) {
  const [day, time] = String(value || "").split("T");
  return { day: day || "", time: time ? time.slice(0, 5) : "" };
}

// Turn a PascalCase key into a readable header ("PromotionName" -> "Promotion Name").
function humanizeKey(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
}

// PascalCase key -> camelCase server param ("PromotionName" -> "promotionName").
function toParamName(key) {
  return key ? key.charAt(0).toLowerCase() + key.slice(1) : key;
}

// Scalar value -> display text, with a "-" fallback for empties.
function cellText(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// A single rating's answer as display text: numeric-scale answers become "N/5",
// everything else is shown verbatim. Empty answers fall back to "-".
function formatRatingAnswer(r) {
  const ans = r.Answer;
  if (ans === null || ans === undefined || ans === "") return "-";
  if (r.AnswerType === RATING_SCALE_ANSWER_TYPE) {
    return `${ans}/${RATING_SCALE_MAX}`;
  }
  return String(ans);
}

// Playable recordings only: many come back with RecordUrl:null / Status:2 while
// still processing and can't be played.
function playableRecordings(m) {
  return (m.Recordings || [])
    .filter((r) => r.RecordUrl)
    .map((r) => ({ Id: r.Id, RecordUrl: r.RecordUrl, Status: r.Status }));
}

export default function AllCoachMeetings() {
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem('user'));
  const userAdminToken = sessionStorage.getItem("jwtP");
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 700px)" });

  // Server-driven page state (raw backend rows kept as-is for dynamic columns).
  const [meetings, setMeetings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [nav, setNav] = useState(false);

  // Filters (all applied server-side).
  // selectedCoachId is the coach profile's Id (unique per exam-category profile)
  // and is what the meetings filter sends. selectedCoachUserId is that coach's
  // UserId, used only to fetch the coach's students.
  const [selectedCoachId, setSelectedCoachId] = useState("");
  const [selectedCoachUserId, setSelectedCoachUserId] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [dateRange, setDateRange] = useState({ minDate: "", maxDate: "" });
  // Per-column text filters: `textInputs` is what's typed (immediate, controlled)
  // and `textFilters` is the debounced, applied copy that drives the request.
  const [textInputs, setTextInputs] = useState({});
  const [textFilters, setTextFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  // Bumped on reset to remount the (uncontrolled) date picker so it clears too.
  const [resetNonce, setResetNonce] = useState(0);

  // Filter dropdown option lists (fetched separately, not derived from a page).
  const [coachOptions, setCoachOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Recording modal state.
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [activeRecordings, setActiveRecordings] = useState([]);
  const [activeRecordingUrl, setActiveRecordingUrl] = useState(null);

  // Guards against out-of-order responses clobbering fresh data.
  const meetingsReqRef = useRef(0);
  const studentsReqRef = useRef(0);
  // Mirror of the applied text filters, to debounce without redundant fetches.
  const appliedTextRef = useRef({});

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  /* ---- Coach options (once) --------------------------------------------- */
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await GetAllCoaches(userAdminToken);
        if (!active) return;
        if (res && res.data && res.data.ResponseStatus === 1) {
          const opts = (res.data.ResponseData || [])
            // The same coach appears once per exam-category profile: same UserId,
            // but a distinct Id. The meetings filter is by the profile's Id, so
            // that's the option value (also unique, so no duplicate values). We
            // keep UserId separately for fetching that coach's students, and
            // append the exam category to the label — e.g. "Batuhan / YKS".
            .map((c) => {
              const name = joinName(c.Name, c.Surname);
              const cat = examCategoryLabel(c.ExamCategoryId);
              return {
                id: c.Id,
                userId: c.UserId,
                label: cat ? `${name} / ${cat}` : name,
              };
            })
            .filter((c) => c.id != null)
            .sort((a, b) =>
              a.label.localeCompare(b.label, "tr", { sensitivity: "base" })
            );
          setCoachOptions(opts);
        }
      } catch (err) {
        console.error("GetAllCoaches (coach options) error", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [userAdminToken]);

  /* ---- Student options (scoped to selected coach) ----------------------- */
  useEffect(() => {
    // Student filter only makes sense once a coach is chosen. Students are
    // fetched by the coach's UserId (not the profile Id).
    if (!selectedCoachUserId) {
      setStudentOptions([]);
      return;
    }
    const reqId = ++studentsReqRef.current;
    setStudentsLoading(true);
    (async () => {
      try {
        const res = await GetCoachStudents(userAdminToken, selectedCoachUserId);
        if (reqId !== studentsReqRef.current) return; // stale
        if (res && res.data && res.data.ResponseStatus === 1) {
          const opts = (res.data.ResponseData || [])
            .map((s) => ({
              id: s.UserId,
              name: (s.Name + " " + (s.Surname || "")).trim(),
            }))
            .filter((s) => s.id != null)
            .sort((a, b) =>
              a.name.localeCompare(b.name, "tr", { sensitivity: "base" })
            );
          setStudentOptions(opts);
        } else {
          setStudentOptions([]);
        }
      } catch (err) {
        console.error("GetCoachStudents (student options) error", err);
        setStudentOptions([]);
      } finally {
        if (reqId === studentsReqRef.current) setStudentsLoading(false);
      }
    })();
  }, [userAdminToken, selectedCoachUserId]);

  /* ---- Debounce typed text filters -> applied filters ------------------- */
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = {};
      Object.keys(textInputs).forEach((k) => {
        const v = (textInputs[k] || "").trim();
        if (v) next[k] = v;
      });
      if (JSON.stringify(appliedTextRef.current) !== JSON.stringify(next)) {
        appliedTextRef.current = next;
        setTextFilters(next);
        setCurrentPage(1);
      }
    }, TEXT_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [textInputs]);

  /* ---- Meetings page (refetch on page/filter change) -------------------- */
  useEffect(() => {
    const reqId = ++meetingsReqRef.current;
    setLoading(true);
    (async () => {
      try {
        // Send null (not "") for unset filters, matching the API contract.
        // The coach filter sends the coach profile's Id (selectedCoachId).
        const params = {
          pageNumber: currentPage,
          pageSize: PAGE_SIZE,
          coachUserId: selectedCoachId ? Number(selectedCoachId) : null,
          studentUserId: selectedStudentId ? Number(selectedStudentId) : null,
          minDate: toDateParam(dateRange.minDate) || null,
          maxDate: toDateParam(dateRange.maxDate) || null,
        };
        // Dynamic per-column text filters (e.g. title, promotionName).
        Object.keys(textFilters).forEach((param) => {
          params[param] = textFilters[param] || null;
        });

        const res = await GetAllCoachMeetings(userAdminToken, params);
        if (reqId !== meetingsReqRef.current) return; // stale

        // The endpoint returns a flat PascalCase body { PageNumber, PageSize,
        // PageCount, TotalItemCount, Items }. Fall back to an enveloped
        // ResponseData / camelCase shape defensively.
        const body =
          res && res.data ? res.data.ResponseData || res.data : null;
        const rawItems = body
          ? Array.isArray(body.Items)
            ? body.Items
            : Array.isArray(body.items)
            ? body.items
            : null
          : null;

        if (rawItems) {
          const total =
            body.TotalItemCount != null
              ? body.TotalItemCount
              : body.totalItemCount;
          setMeetings(rawItems);
          setTotalCount(typeof total === "number" ? total : rawItems.length);
        } else {
          toast.error("Toplantı kayıtları alınamadı.");
          setMeetings([]);
          setTotalCount(0);
        }
      } catch (error) {
        if (reqId !== meetingsReqRef.current) return;
        console.error("GetAllCoachMeetings error", error);
        toast.error("Bir hata oluştu, lütfen tekrar deneyiniz.");
        setMeetings([]);
        setTotalCount(0);
      } finally {
        if (reqId === meetingsReqRef.current) setLoading(false);
      }
    })();
  }, [
    userAdminToken,
    currentPage,
    selectedCoachId,
    selectedStudentId,
    dateRange,
    textFilters,
  ]);

  /* ---- Filter handlers -------------------------------------------------- */
  function handleCoachChange(e) {
    const id = e.target.value;
    const opt = coachOptions.find((c) => String(c.id) === String(id));
    setSelectedCoachId(id);
    setSelectedCoachUserId(opt && opt.userId != null ? String(opt.userId) : "");
    setSelectedStudentId(""); // student list depends on coach
    setCurrentPage(1);
  }

  function handleStudentChange(e) {
    setSelectedStudentId(e.target.value);
    setCurrentPage(1);
  }

  function handleDateRangeChange({ minDate, maxDate }) {
    setDateRange({ minDate, maxDate });
    setCurrentPage(1);
  }

  function handleTextFilterChange(param, value) {
    setTextInputs((prev) => ({ ...prev, [param]: value }));
    // Page reset + applied-filter update happen after the debounce.
  }

  const hasActiveFilters =
    !!selectedCoachId ||
    !!selectedStudentId ||
    !!dateRange.minDate ||
    !!dateRange.maxDate ||
    Object.values(textInputs).some((v) => v && v.trim() !== "");

  function handleResetFilters() {
    setSelectedCoachId("");
    setSelectedCoachUserId("");
    setSelectedStudentId("");
    setDateRange({ minDate: "", maxDate: "" });
    setTextInputs({});
    appliedTextRef.current = {};
    setTextFilters({});
    setCurrentPage(1);
    setResetNonce((n) => n + 1); // remount date picker to clear its display
  }

  /* ---- Recording modal -------------------------------------------------- */
  function handleOpenRecording(recordings) {
    if (!recordings || recordings.length === 0) return;
    setActiveRecordings(recordings);
    setActiveRecordingUrl(recordings[0].RecordUrl);
    setIsVideoOpen(true);
  }

  function handleCloseRecording() {
    setIsVideoOpen(false);
    setActiveRecordings([]);
    setActiveRecordingUrl(null);
  }

  /* ---- Column config (core + auto-detected) ----------------------------- */
  function buildColumns(rows) {
    const coreLeading = [
      {
        id: "Date",
        header: "Tarih",
        filter: { kind: "date" },
        render: (m) => {
          const { day, time } = splitDateTime(m.Date);
          return (
            <div className="meeting-date">
              <div>
                {day
                  ? new Date(day).toLocaleDateString("tr-TR", {
                      month: "short",
                      day: "2-digit",
                      year: "numeric",
                    })
                  : "-"}
              </div>
              <div className="meeting-time">{time}</div>
            </div>
          );
        },
      },
      {
        id: "Coach",
        header: "Koç",
        cellClassName: "meeting-coach",
        filter: { kind: "coach" },
        render: (m) => joinName(m.CoachName, m.CoachSurname),
      },
      {
        id: "Student",
        header: "Öğrenci",
        filter: { kind: "student" },
        render: (m) => joinName(m.StudentName, m.StudentSurname),
      },
      {
        id: "Title",
        header: "Toplantı Başlığı",
        filter: { kind: "text", param: "title" },
        render: (m) => cellText(m.Title),
      },
    ];

    const coreTrailing = [
      {
        id: "Ratings",
        header: "Değerlendirme",
        cellClassName: "meeting-ratings-cell",
        filter: null,
        render: (m) => {
          const ratings = (m.Ratings || [])
            .slice()
            .sort((a, b) => (a.Order || 0) - (b.Order || 0));
          if (ratings.length === 0) return "-";
          return (
            <div className="meeting-ratings">
              {ratings.map((r) => (
                <div
                  className="meeting-rating-item"
                  key={r.CoachMeetingRatingId}
                >
                  <span className="meeting-rating-q">
                    {r.Text}
                    {r.Description ? ` (${r.Description})` : ""}
                  </span>
                  <span className="meeting-rating-a">
                    {formatRatingAnswer(r)}
                  </span>
                </div>
              ))}
            </div>
          );
        },
      },
      {
        id: "Recordings",
        header: "Aksiyonlar",
        cellClassName: "meeting-actions",
        filter: null,
        render: (m) => {
          const recs = playableRecordings(m);
          return (
            <div className="recording-btn-wrapper">
              <button
                className="btn-recording active"
                disabled={recs.length === 0}
                onClick={() => handleOpenRecording(recs)}
              >
                Toplantı Kaydı
              </button>
              {recs.length === 0 && (
                <span className="recording-tooltip">Toplantı kaydı yoktur</span>
              )}
            </div>
          );
        },
      },
    ];

    // Discover any extra keys the backend returns (in first-seen order).
    const autoKeys = [];
    const seen = new Set();
    (rows || []).forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (
          !CORE_CONSUMED_KEYS.has(key) &&
          !HIDDEN_KEYS.has(key) &&
          !seen.has(key)
        ) {
          seen.add(key);
          autoKeys.push(key);
        }
      });
    });

    const autoColumns = autoKeys.map((key) => ({
      id: key,
      header: AUTO_COLUMN_LABELS[key] || humanizeKey(key),
      filter: { kind: "text", param: toParamName(key) },
      render: (m) => cellText(m[key]),
    }));

    return [...coreLeading, ...autoColumns, ...coreTrailing];
  }

  const columns = buildColumns(meetings);

  function renderFilterControl(col) {
    const f = col.filter;
    if (!f) return null;

    if (f.kind === "coach") {
      return (
        <div className="meetings-filter-select-wrapper">
          <select
            className="meetings-filter-select"
            value={selectedCoachId}
            onChange={handleCoachChange}
          >
            <option value="">Tüm Koçlar</option>
            {coachOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (f.kind === "student") {
      return (
        <div className="meetings-filter-select-wrapper">
          <select
            className="meetings-filter-select"
            value={selectedStudentId}
            onChange={handleStudentChange}
            disabled={!selectedCoachId || studentsLoading}
          >
            <option value="">
              {selectedCoachId
                ? studentsLoading
                  ? "Yükleniyor…"
                  : "Tüm Öğrenciler"
                : "Önce koç seçiniz"}
            </option>
            {studentOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (f.kind === "date") {
      return (
        <DateRangePicker
          key={resetNonce}
          onChangeDateRange={handleDateRangeChange}
        />
      );
    }

    // text
    return (
      <input
        type="text"
        className="col-filter-input"
        placeholder="Ara…"
        value={textInputs[f.param] || ""}
        onChange={(e) => handleTextFilterChange(f.param, e.target.value)}
      />
    );
  }

  return (
    <>
      {isTabletOrMobile ? (
        <div>
          <span
            style={{ position: "absolute", top: 10, right: 10 }}
            onClick={() => setNav(!nav)}
          >
            menu
          </span>
          {nav && <SideBar />}
        </div>
      ) : (
        <SideBar />
      )}
      <ToastContainer autoClose={5000} />

      <div
        className="coaches-page"
        style={{
          paddingLeft: isTabletOrMobile ? 15 : Components.SideBarWidth + 15,
        }}
      >
        <div className="coaches-page-container">
          <BackButton />

          {/* Breadcrumb */}
          <div className="coach-profile-breadcrumb">
            <span
              className="breadcrumb-link"
              onClick={() => history.push("/CoachesAdminPanel")}
            >
              Koçlar
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Tüm Toplantı Kayıtları</span>
          </div>

          <PageHeader title="Tüm Toplantı Kayıtları" />

          <div className="coach-profile-card">
            {/* Header + reset */}
            <div className="meetings-header">
              <h3>Tüm Koçların Toplantıları</h3>

              <button
                type="button"
                className="meetings-reset-btn"
                onClick={handleResetFilters}
                disabled={!hasActiveFilters}
              >
                Filtreleri Temizle
              </button>
            </div>

            {/* Result count */}
            <div className="meetings-result-count">
              {loading ? "Yükleniyor…" : `${totalCount} toplantı`}
            </div>

            {/* Table (columns + per-column filter row are both dynamic) */}
            <table className="meetings-table all-meetings-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.id}>{col.header}</th>
                  ))}
                </tr>
                <tr className="meetings-filter-row">
                  {columns.map((col) => (
                    <th key={col.id}>{renderFilterControl(col)}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center" }}>
                      Toplantılar yükleniyor…
                    </td>
                  </tr>
                ) : meetings.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center" }}>
                      Toplantı kaydı bulunamadı
                    </td>
                  </tr>
                ) : (
                  meetings.map((m) => (
                    <tr key={m.MeetingId}>
                      {columns.map((col) => (
                        <td key={col.id} className={col.cellClassName}>
                          {col.render(m)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Recording modal */}
            {isVideoOpen && (
              <div
                className="video-modal-backdrop"
                onClick={handleCloseRecording}
              >
                <div
                  className="video-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="video-modal-close"
                    onClick={handleCloseRecording}
                  >
                    ✕
                  </button>

                  <div className="video-modal-player">
                    <video
                      key={activeRecordingUrl}
                      src={activeRecordingUrl}
                      controls
                      autoPlay
                    />
                  </div>

                  {activeRecordings.length > 1 && (
                    <div className="video-modal-list">
                      {activeRecordings.map(function (r, index) {
                        const isActive = r.RecordUrl === activeRecordingUrl;
                        return (
                          <div
                            key={r.Id}
                            className={
                              "video-modal-list-item " +
                              (isActive ? "active" : "")
                            }
                            onClick={() => setActiveRecordingUrl(r.RecordUrl)}
                          >
                            <span>Kayıt {index + 1}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={PAGE_SIZE}
              totalItems={totalCount}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </>
  );
}
